import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

export default function GiftCardManagementForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  const MIN_GIFT_CARD_PRICE = 1000;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    price_type: "range",
    single_price: "",
    min_price: "",
    max_price: "",
    discount_type: "PERCENT",
    discount_value: "",
    status: "1",
  });

  const fetchGiftCard = async () => {
    try {
      setFetching(true);

      const res = await api.get(`/admin/gift-card-management/${id}`);
      const data = res.data?.data;

      setForm({
        title: data?.title || "",
        description: data?.description || "",
        price_type: data?.price_type || "range",
        single_price: data?.single_price?.toString() || "",
        min_price: data?.min_price?.toString() || "",
        max_price: data?.max_price?.toString() || "",
        discount_type: data?.discount_type || "PERCENT",
        discount_value: data?.discount_value?.toString() || "",
        status: String(data?.status ?? "1"),
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e.message ||
          "Failed to load gift card management"
      );
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (id) fetchGiftCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onChange = (e) => {
    const { name, value } = e.target;

    // If switching price_type, clear irrelevant fields (prevents stale values)
    if (name === "price_type") {
      setForm((p) => ({
        ...p,
        price_type: value,
        single_price: value === "single" ? p.single_price : "",
        min_price: value === "range" ? p.min_price : "",
        max_price: value === "range" ? p.max_price : "",
      }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const validate = () => {
    if (!form.title || !form.title.trim()) {
      toast.error("Title is required");
      return false;
    }

    const priceType = form.price_type || "range";

    if (!["single", "range"].includes(priceType)) {
      toast.error("Price type must be either Single or Range");
      return false;
    }

    if (priceType === "single") {
      const singlePrice = toNum(form.single_price);

      if (singlePrice === null) {
        toast.error("Single price is required");
        return false;
      }
      if (singlePrice <= 0) {
        toast.error("Single price must be a valid positive number");
        return false;
      }
      if (singlePrice < MIN_GIFT_CARD_PRICE) {
        toast.error(`Single price must be at least ₹${MIN_GIFT_CARD_PRICE}`);
        return false;
      }
    } else {
      const minPrice = toNum(form.min_price);
      const maxPrice = toNum(form.max_price);

      if (minPrice === null || maxPrice === null) {
        toast.error("Min price and Max price are required");
        return false;
      }
      if (minPrice <= 0) {
        toast.error("Min price must be a valid positive number");
        return false;
      }
      if (minPrice < MIN_GIFT_CARD_PRICE) {
        toast.error(`Min price must be at least ₹${MIN_GIFT_CARD_PRICE}`);
        return false;
      }
      if (maxPrice <= 0) {
        toast.error("Max price must be a valid positive number");
        return false;
      }
      if (maxPrice < MIN_GIFT_CARD_PRICE) {
        toast.error(`Max price must be at least ₹${MIN_GIFT_CARD_PRICE}`);
        return false;
      }
      if (maxPrice <= minPrice) {
        toast.error("Max price must be greater than min price");
        return false;
      }
    }

    // Discount validation
    if (form.discount_type && !["PERCENT", "FLAT"].includes(form.discount_type)) {
      toast.error("Discount type must be either PERCENT or FLAT");
      return false;
    }

    if (form.discount_value !== "" && form.discount_value !== null && form.discount_value !== undefined) {
      const discountVal = toNum(form.discount_value);
      if (discountVal === null || discountVal < 0) {
        toast.error("Discount value must be a valid positive number");
        return false;
      }
      if (form.discount_type === "PERCENT" && discountVal > 100) {
        toast.error("Discount percentage cannot exceed 100");
        return false;
      }
    }

    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // ✅ our toast validations will run now
    if (!validate()) return;

    const priceType = form.price_type || "range";

    try {
      setLoading(true);

      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        price_type: priceType,
        single_price: priceType === "single" ? Number(form.single_price) : null,
        min_price: priceType === "range" ? Number(form.min_price) : null,
        max_price: priceType === "range" ? Number(form.max_price) : null,
        discount_type: form.discount_type || null,
        discount_value: form.discount_value ? Number(form.discount_value) : null,
        status: Number(form.status),
      };

      if (id) {
        await api.patch(`/admin/gift-card-management/${id}`, payload);
        toast.success("Gift card management updated");
      } else {
        await api.post("/admin/gift-card-management", payload);
        toast.success("Gift card management created");
      }

      navigate("/admin/gift-card-management");
    } catch (e2) {
      const errorMsg = e2?.response?.data?.message || e2.message || "Save failed";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            {id ? "Edit Gift Card Management" : "Create Gift Card Management"}
          </h1>
        </div>

        <button
          type="button"
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          onClick={() => navigate("/admin/gift-card-management")}
        >
          Back
        </button>
      </div>

      {fetching ? (
        <div className="mt-6 text-sm text-gray-600">Loading gift card management...</div>
      ) : (
        // ✅ noValidate disables browser validation popups
        <form noValidate onSubmit={onSubmit} className="mt-6 border rounded-2xl p-5 space-y-4">
          <div>
            <label className="text-sm font-semibold">Title *</label>
            <input
              name="title"
              value={form.title}
              onChange={onChange}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
              placeholder="Ex: Standard Gift Card"
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={onChange}
              rows={4}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
              placeholder="Enter description for this gift card..."
            />
          </div>

          <div>
            <label className="text-sm font-semibold">Price Type *</label>
            <select
              name="price_type"
              value={form.price_type}
              onChange={onChange}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            >
              <option value="range">Range</option>
              <option value="single">Single</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {form.price_type === "single"
                ? "Enter a single price value"
                : "Enter min and max price range"}
            </p>
          </div>

          {form.price_type === "single" ? (
            <div>
              <label className="text-sm font-semibold">Single Price (₹) *</label>
              <input
                type="number"
                name="single_price"
                value={form.single_price}
                onChange={onChange}
                step="0.01"
                className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                placeholder={`>= ${MIN_GIFT_CARD_PRICE}`}
              />
              <p className="mt-1 text-xs text-gray-500">Minimum ₹{MIN_GIFT_CARD_PRICE}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">Min Price (₹) *</label>
                <input
                  type="number"
                  name="min_price"
                  value={form.min_price}
                  onChange={onChange}
                  step="0.01"
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  placeholder={`>= ${MIN_GIFT_CARD_PRICE}`}
                />
                <p className="mt-1 text-xs text-gray-500">Minimum ₹{MIN_GIFT_CARD_PRICE}</p>
              </div>

              <div>
                <label className="text-sm font-semibold">Max Price (₹) *</label>
                <input
                  type="number"
                  name="max_price"
                  value={form.max_price}
                  onChange={onChange}
                  step="0.01"
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                  placeholder={`>= ${MIN_GIFT_CARD_PRICE}`}
                />
                <p className="mt-1 text-xs text-gray-500">Minimum ₹{MIN_GIFT_CARD_PRICE}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold">Discount Type</label>
              <select
                name="discount_type"
                value={form.discount_type}
                onChange={onChange}
                className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
              >
                <option value="">Select Discount Type</option>
                <option value="PERCENT">Percentage (%)</option>
                <option value="FLAT">Flat Amount (₹)</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold">Discount Value</label>
              <input
                type="number"
                name="discount_value"
                value={form.discount_value}
                onChange={onChange}
                min="0"
                step="0.01"
                max={form.discount_type === "PERCENT" ? 100 : undefined}
                className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                placeholder={form.discount_type === "PERCENT" ? "0-100" : "0.00"}
                disabled={!form.discount_type}
              />
              {form.discount_type === "PERCENT" && (
                <p className="mt-1 text-xs text-gray-500">Enter percentage (0-100)</p>
              )}
            </div>
          </div>

          <div className="max-w-[240px]">
            <label className="text-sm font-semibold">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={onChange}
              className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>

            <button
              type="button"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
              onClick={() => navigate("/admin/gift-card-management")}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
